// Custom two-tone icon set — the product's own marks, not a stock library.
//
// Every icon is exactly two colours, always the same two, taken from the
// theme tokens so they re-tune themselves in dark mode:
//
//   .icc-f  filled body   → --c-accent (amber)
//   .icc-l  line work     → --c-brand  (blue)
//
// Because the colours are fixed rather than `currentColor`, an icon looks the
// same in a muted table row as in a hero card — that consistency is the point.
// Styles live in index.css next to the tokens.
//
// The exported names deliberately mirror the lucide names they replaced, so a
// call site only changes its import path. Pure UI chrome (chevrons, close,
// copy, arrows, hamburger, trash) intentionally stays on lucide: below ~16px
// the second colour cannot resolve, and chrome should recede, not compete.
//
// Geometry is kept to circles, rounded rects and polylines on a 24×24 grid.
// That reads more cleanly at 15px than fine bezier detail would.
import type { CSSProperties, ReactNode } from "react";

/** Matches the lucide prop shape closely enough that call sites only change
 *  their import path. `style` is accepted for layout, but note the two
 *  colours are fixed by CSS — a `style={{ color }}` no longer tints an icon. */
export interface IconProps {
  size?: number | string;
  className?: string;
  style?: CSSProperties;
}

function make(fill: ReactNode, line: ReactNode, displayName: string) {
  function Icon({ size = 20, className = "", style }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        className={`icc-icon ${className}`}
        style={style}
        aria-hidden="true"
        focusable="false"
      >
        <g className="icc-f">{fill}</g>
        <g className="icc-l">{line}</g>
      </svg>
    );
  }
  Icon.displayName = displayName;
  return Icon;
}

/* ---------------------------------------------------------------- energy */

export const Zap = make(
  <path d="M13.4 2.4 5.2 13.2h5.1l-.7 8.4 8.2-10.8h-5.1z" />,
  <path d="M13.4 2.4 5.2 13.2h5.1l-.7 8.4 8.2-10.8h-5.1z" />,
  "Zap",
);

export const Battery = make(
  <rect x="2.5" y="7.5" width="15" height="9" rx="2.6" />,
  <>
    <rect x="2.5" y="7.5" width="15" height="9" rx="2.6" />
    <path d="M20 10.4v3.2" />
  </>,
  "Battery",
);

export const BatteryCharging = make(
  <rect x="2.5" y="7.5" width="15" height="9" rx="2.6" />,
  <>
    <rect x="2.5" y="7.5" width="15" height="9" rx="2.6" />
    <path d="M20 10.4v3.2" />
    <path d="M10.6 9.6 7.9 12.6h3.2l-.5 2.4 2.9-3.1h-3.2z" />
  </>,
  "BatteryCharging",
);

export const PlugZap = make(
  <path d="M7 3.5h10v6a5 5 0 0 1-10 0z" />,
  <>
    <path d="M7 3.5h10v6a5 5 0 0 1-10 0z" />
    <path d="M9.6 3.5V1.8M14.4 3.5V1.8M12 14.5v2.2" />
    <path d="M12.9 17.2 10.4 20h2.9l-.4 2.2 2.6-2.9h-2.9z" />
  </>,
  "PlugZap",
);

export const Plug = make(
  <path d="M7 3.5h10v6a5 5 0 0 1-10 0z" />,
  <>
    <path d="M7 3.5h10v6a5 5 0 0 1-10 0z" />
    <path d="M9.6 3.5V1.8M14.4 3.5V1.8M12 14.5v7.7" />
  </>,
  "Plug",
);

export const Sun = make(
  <circle cx="12" cy="12" r="4.6" />,
  <>
    <circle cx="12" cy="12" r="4.6" />
    <path d="M12 1.8v2.4M12 19.8v2.4M1.8 12h2.4M19.8 12h2.4M4.8 4.8l1.7 1.7M17.5 17.5l1.7 1.7M19.2 4.8l-1.7 1.7M6.5 17.5l-1.7 1.7" />
  </>,
  "Sun",
);

export const Moon = make(
  <path d="M20 14.6A8.6 8.6 0 0 1 9.4 4a8.6 8.6 0 1 0 10.6 10.6z" />,
  <path d="M20 14.6A8.6 8.6 0 0 1 9.4 4a8.6 8.6 0 1 0 10.6 10.6z" />,
  "Moon",
);

export const CloudSun = make(
  <circle cx="8.2" cy="7.4" r="3.6" />,
  <>
    <circle cx="8.2" cy="7.4" r="3.6" />
    <path d="M8.2 1.6v1.4M2.4 7.4h1.4M4.1 3.3l1 1M12.3 3.3l-1 1" />
    <path d="M10.4 20.4h7.2a3.6 3.6 0 0 0 .3-7.2 5 5 0 0 0-9.5 1.1 3 3 0 0 0 2 6.1z" />
  </>,
  "CloudSun",
);

export const Flame = make(
  <path d="M12 2.6c3.4 3.4 6 5.9 6 9.6a6 6 0 0 1-12 0c0-2.3 1.2-4 3-6 .3 1.4 1 2.3 1.9 2.6C11.4 6.6 11.5 4.6 12 2.6z" />,
  <path d="M12 2.6c3.4 3.4 6 5.9 6 9.6a6 6 0 0 1-12 0c0-2.3 1.2-4 3-6 .3 1.4 1 2.3 1.9 2.6C11.4 6.6 11.5 4.6 12 2.6z" />,
  "Flame",
);

export const Wind = make(
  <circle cx="18.4" cy="8" r="2.4" />,
  <>
    <path d="M2.6 8h13.4M2.6 12.6h9.8M2.6 17.2h7.6" />
    <circle cx="18.4" cy="8" r="2.4" />
    <path d="M14.6 15.4a2.2 2.2 0 1 1 1.7 3.6" />
  </>,
  "Wind",
);

export const AirVent = make(
  <rect x="2.5" y="3.5" width="19" height="9" rx="2.4" />,
  <>
    <rect x="2.5" y="3.5" width="19" height="9" rx="2.4" />
    <path d="M6 7.3h12M6 9.6h12" />
    <path d="M8.5 16.2a2 2 0 1 0 3.4 1.6v2.7M15.2 15.6v4.9" />
  </>,
  "AirVent",
);

/* ------------------------------------------------------------- hardware */

export const Cpu = make(
  <rect x="6.5" y="6.5" width="11" height="11" rx="2.2" />,
  <>
    <rect x="6.5" y="6.5" width="11" height="11" rx="2.2" />
    <rect x="10" y="10" width="4" height="4" rx="1" />
    <path d="M9.4 2.6v3.9M14.6 2.6v3.9M9.4 17.5v3.9M14.6 17.5v3.9M2.6 9.4h3.9M2.6 14.6h3.9M17.5 9.4h3.9M17.5 14.6h3.9" />
  </>,
  "Cpu",
);

export const HardDrive = make(
  <rect x="2.5" y="12.5" width="19" height="7" rx="2.2" />,
  <>
    <path d="M5.4 4.6h13.2l2.9 7.9" />
    <path d="M2.5 12.5 5.4 4.6" />
    <rect x="2.5" y="12.5" width="19" height="7" rx="2.2" />
    <circle cx="6.6" cy="16" r="1.1" />
    <path d="M10.5 16h7.5" />
  </>,
  "HardDrive",
);

export const Cable = make(
  <rect x="3" y="2.8" width="5.4" height="6.4" rx="1.6" />,
  <>
    <rect x="3" y="2.8" width="5.4" height="6.4" rx="1.6" />
    <path d="M4.4 2.8V1.4M7 2.8V1.4" />
    <path d="M5.7 9.2v4.4a5 5 0 0 0 5 5h1.6a5 5 0 0 1 5 5" />
    <rect x="15.6" y="14.8" width="5.4" height="6.4" rx="1.6" />
    <path d="M17 21.2v1.4M19.6 21.2v1.4" />
  </>,
  "Cable",
);

export const ToggleRight = make(
  <rect x="2.2" y="6.6" width="19.6" height="10.8" rx="5.4" />,
  <>
    <rect x="2.2" y="6.6" width="19.6" height="10.8" rx="5.4" />
    <circle cx="16.4" cy="12" r="3" />
  </>,
  "ToggleRight",
);

export const Thermometer = make(
  <path d="M12 3.4a2.4 2.4 0 0 1 2.4 2.4v7.6a4.6 4.6 0 1 1-4.8 0V5.8A2.4 2.4 0 0 1 12 3.4z" />,
  <>
    <path d="M12 3.4a2.4 2.4 0 0 1 2.4 2.4v7.6a4.6 4.6 0 1 1-4.8 0V5.8A2.4 2.4 0 0 1 12 3.4z" />
    <path d="M12 8.6v7.6" />
  </>,
  "Thermometer",
);

export const Ruler = make(
  <rect x="1.6" y="8.4" width="20.8" height="7.2" rx="1.8" />,
  <>
    <rect x="1.6" y="8.4" width="20.8" height="7.2" rx="1.8" />
    <path d="M6.2 8.4v3M10.1 8.4v4.4M14 8.4v3M17.9 8.4v4.4" />
  </>,
  "Ruler",
);

export const Rows3 = make(
  <rect x="2.4" y="3.4" width="19.2" height="5" rx="1.6" />,
  <>
    <rect x="2.4" y="3.4" width="19.2" height="5" rx="1.6" />
    <rect x="2.4" y="9.5" width="19.2" height="5" rx="1.6" />
    <rect x="2.4" y="15.6" width="19.2" height="5" rx="1.6" />
  </>,
  "Rows3",
);

export const Wrench = make(
  <path d="M20.4 5.2 17 8.6l-1.6-1.6 3.4-3.4a5 5 0 0 0-6.5 6.1L4.4 17.6a2 2 0 0 0 2.8 2.8l7.9-7.9a5 5 0 0 0 5.3-7.3z" />,
  <path d="M20.4 5.2 17 8.6l-1.6-1.6 3.4-3.4a5 5 0 0 0-6.5 6.1L4.4 17.6a2 2 0 0 0 2.8 2.8l7.9-7.9a5 5 0 0 0 5.3-7.3z" />,
  "Wrench",
);

export const QrCode = make(
  <>
    <rect x="3" y="3" width="7" height="7" rx="1.6" />
    <rect x="14" y="3" width="7" height="7" rx="1.6" />
    <rect x="3" y="14" width="7" height="7" rx="1.6" />
  </>,
  <>
    <rect x="3" y="3" width="7" height="7" rx="1.6" />
    <rect x="14" y="3" width="7" height="7" rx="1.6" />
    <rect x="3" y="14" width="7" height="7" rx="1.6" />
    <path d="M14 14h3v3h-3zM20 14h1M14 20h3M20 17.5v3.5" />
  </>,
  "QrCode",
);

/* ------------------------------------------------------------ grid/data */

export const Network = make(
  <>
    <rect x="9" y="1.8" width="6" height="6" rx="1.8" />
    <rect x="1.6" y="16.2" width="6" height="6" rx="1.8" />
    <rect x="16.4" y="16.2" width="6" height="6" rx="1.8" />
  </>,
  <>
    <rect x="9" y="1.8" width="6" height="6" rx="1.8" />
    <rect x="1.6" y="16.2" width="6" height="6" rx="1.8" />
    <rect x="16.4" y="16.2" width="6" height="6" rx="1.8" />
    <path d="M12 7.8v4.4M4.6 16.2v-2.2h14.8v2.2" />
  </>,
  "Network",
);

export const Wifi = make(
  <circle cx="12" cy="18.6" r="1.9" />,
  <>
    <path d="M2.2 8.6a14 14 0 0 1 19.6 0" />
    <path d="M5.6 12.4a9.2 9.2 0 0 1 12.8 0" />
    <circle cx="12" cy="18.6" r="1.9" />
  </>,
  "Wifi",
);

export const WifiOff = make(
  <circle cx="12" cy="18.6" r="1.9" />,
  <>
    <path d="M2.2 8.6a14 14 0 0 1 5.1-3.2M16.6 5.6a14 14 0 0 1 5.2 3" />
    <path d="M8.2 13.4a9.2 9.2 0 0 1 7.4-.4" />
    <circle cx="12" cy="18.6" r="1.9" />
    <path d="M2.6 2.6 21.4 21.4" />
  </>,
  "WifiOff",
);

export const Cloud = make(
  <path d="M7.2 19.4h10.4a4 4 0 0 0 .4-8 5.6 5.6 0 0 0-10.6 1.2 3.4 3.4 0 0 0-.2 6.8z" />,
  <path d="M7.2 19.4h10.4a4 4 0 0 0 .4-8 5.6 5.6 0 0 0-10.6 1.2 3.4 3.4 0 0 0-.2 6.8z" />,
  "Cloud",
);

export const DatabaseBackup = make(
  <ellipse cx="12" cy="5.6" rx="8" ry="2.8" />,
  <>
    <ellipse cx="12" cy="5.6" rx="8" ry="2.8" />
    <path d="M4 5.6v6c0 1.8 3.6 3.2 8 3.2M20 5.6v5.2" />
    <path d="M15.6 18.6a3.6 3.6 0 1 0 1-3.4" />
    <path d="M16.2 12.2v3h3" />
  </>,
  "DatabaseBackup",
);

export const Share2 = make(
  <>
    <circle cx="18.4" cy="5.2" r="2.8" />
    <circle cx="5.6" cy="12" r="2.8" />
    <circle cx="18.4" cy="18.8" r="2.8" />
  </>,
  <>
    <circle cx="18.4" cy="5.2" r="2.8" />
    <circle cx="5.6" cy="12" r="2.8" />
    <circle cx="18.4" cy="18.8" r="2.8" />
    <path d="M8.1 10.7 15.9 6.5M8.1 13.3l7.8 4.2" />
  </>,
  "Share2",
);

export const Send = make(
  <path d="M21.4 2.6 2.6 9.8l7.6 2.8 2.8 7.6z" />,
  <>
    <path d="M21.4 2.6 2.6 9.8l7.6 2.8 2.8 7.6z" />
    <path d="M21.4 2.6 10.2 12.6" />
  </>,
  "Send",
);

export const RefreshCw = make(
  <circle cx="12" cy="12" r="8" />,
  <>
    <path d="M20 12a8 8 0 0 1-13.7 5.6M4 12a8 8 0 0 1 13.7-5.6" />
    <path d="M17.4 2.6v4h-4M6.6 21.4v-4h4" />
  </>,
  "RefreshCw",
);

export const ArrowRightLeft = make(
  <></>,
  <>
    <path d="M3.4 8.2h17.2M17.2 4.8l3.4 3.4-3.4 3.4" />
    <path d="M20.6 15.8H3.4M6.8 12.4l-3.4 3.4 3.4 3.4" />
  </>,
  "ArrowRightLeft",
);

/* --------------------------------------------------------- measurement */

export const Gauge = make(
  <path d="M3 17.4a9.6 9.6 0 1 1 18 0z" />,
  <>
    <path d="M3 17.4a9.6 9.6 0 1 1 18 0z" />
    <path d="M12 17.4 16.2 9" />
    <circle cx="12" cy="17.4" r="1.5" />
  </>,
  "Gauge",
);

export const Activity = make(
  <></>,
  <path d="M2.4 12.4h4.2l2.6-7.6 4.4 14.4 2.6-6.8h5.4" />,
  "Activity",
);

export const HeartPulse = make(
  <path d="M12 20.6 3.9 12.6a4.9 4.9 0 0 1 8.1-5.3 4.9 4.9 0 0 1 8.1 5.3z" />,
  <>
    <path d="M12 20.6 3.9 12.6a4.9 4.9 0 0 1 8.1-5.3 4.9 4.9 0 0 1 8.1 5.3z" />
    <path d="M2.8 12.4h4l1.8-3 2.6 5.6 1.8-3.2h5.6" />
  </>,
  "HeartPulse",
);

export const TrendingUp = make(
  <path d="M3 20V13l5.6 4 5-6.4L21 5.6V20z" />,
  <>
    <path d="M3 17 8.6 13l5 3.4L21 6.6" />
    <path d="M15.4 6.6H21v5.6" />
  </>,
  "TrendingUp",
);

export const TrendingDown = make(
  <path d="M3 4v7l5.6-4 5 6.4L21 18.4V4z" />,
  <>
    <path d="M3 7 8.6 11l5-3.4L21 17.4" />
    <path d="M15.4 17.4H21v-5.6" />
  </>,
  "TrendingDown",
);

export const LineChart = make(
  <path d="M3.4 20.2V17l4.8-3.6 4.4 2.8 7-7.4v11.4z" />,
  <>
    <path d="M3.4 3.4v16.8h17.2" />
    <path d="M6.6 16.4 10.6 12l3.6 2.4 4.8-6" />
  </>,
  "LineChart",
);

export const BarChart3 = make(
  <>
    <rect x="4.4" y="12" width="3.6" height="7.2" rx="1.2" />
    <rect x="10.2" y="7.6" width="3.6" height="11.6" rx="1.2" />
    <rect x="16" y="4" width="3.6" height="15.2" rx="1.2" />
  </>,
  <>
    <rect x="4.4" y="12" width="3.6" height="7.2" rx="1.2" />
    <rect x="10.2" y="7.6" width="3.6" height="11.6" rx="1.2" />
    <rect x="16" y="4" width="3.6" height="15.2" rx="1.2" />
    <path d="M2.6 21.4h18.8" />
  </>,
  "BarChart3",
);

export const Calculator = make(
  <rect x="4" y="2.4" width="16" height="19.2" rx="2.6" />,
  <>
    <rect x="4" y="2.4" width="16" height="19.2" rx="2.6" />
    <rect x="7.2" y="5.6" width="9.6" height="3.4" rx="1" />
    <path d="M8 12.8h.01M12 12.8h.01M16 12.8h.01M8 17h.01M12 17h.01M16 17h.01" />
  </>,
  "Calculator",
);

export const SlidersHorizontal = make(
  <>
    <circle cx="8.4" cy="7" r="2.4" />
    <circle cx="15.6" cy="17" r="2.4" />
  </>,
  <>
    <path d="M2.6 7h3.4M10.8 7h10.6" />
    <path d="M2.6 17h10.6M18 17h3.4" />
    <circle cx="8.4" cy="7" r="2.4" />
    <circle cx="15.6" cy="17" r="2.4" />
  </>,
  "SlidersHorizontal",
);

export const Settings2 = make(
  <circle cx="12" cy="12" r="3.2" />,
  <>
    <circle cx="12" cy="12" r="3.2" />
    <path d="M12 2.4v3M12 18.6v3M2.4 12h3M18.6 12h3M5.2 5.2l2.1 2.1M16.7 16.7l2.1 2.1M18.8 5.2l-2.1 2.1M7.3 16.7l-2.1 2.1" />
  </>,
  "Settings2",
);

/* ------------------------------------------------------------- finance */

export const Wallet = make(
  <rect x="2.6" y="5.6" width="18.8" height="14" rx="3" />,
  <>
    <path d="M2.6 8.6V5.6a2 2 0 0 1 2-2h11.4" />
    <rect x="2.6" y="5.6" width="18.8" height="14" rx="3" />
    <path d="M21.4 10.4h-4.2a2.2 2.2 0 0 0 0 4.4h4.2" />
  </>,
  "Wallet",
);

export const PiggyBank = make(
  <path d="M3.4 12.6a7 7 0 0 1 7-7h3.2a7 7 0 0 1 6.6 4.7l1.4.6v3.4l-1.7.3a7 7 0 0 1-2.5 3v2.2h-3v-1.4h-4.4v1.4h-3v-2.4a7 7 0 0 1-3.6-4.8z" />,
  <>
    <path d="M3.4 12.6a7 7 0 0 1 7-7h3.2a7 7 0 0 1 6.6 4.7l1.4.6v3.4l-1.7.3a7 7 0 0 1-2.5 3v2.2h-3v-1.4h-4.4v1.4h-3v-2.4a7 7 0 0 1-3.6-4.8z" />
    <path d="M16.4 11.2h.01M9.6 5.6a3 3 0 0 1 4.4-2.4" />
  </>,
  "PiggyBank",
);

export const CreditCard = make(
  <rect x="2.2" y="4.6" width="19.6" height="14.8" rx="2.8" />,
  <>
    <rect x="2.2" y="4.6" width="19.6" height="14.8" rx="2.8" />
    <path d="M2.2 9.8h19.6" />
    <path d="M6 15.2h3.4" />
  </>,
  "CreditCard",
);

export const CalendarClock = make(
  <rect x="2.6" y="4.6" width="18.8" height="16.8" rx="2.8" />,
  <>
    <rect x="2.6" y="4.6" width="18.8" height="16.8" rx="2.8" />
    <path d="M2.6 9.6h18.8M7.6 2.6v4M16.4 2.6v4" />
    <circle cx="16.2" cy="15.8" r="3.6" />
    <path d="M16.2 14v1.8l1.3.9" />
  </>,
  "CalendarClock",
);

/* --------------------------------------------------------------- trust */

export const Shield = make(
  <path d="M12 2.4 4 5.6v6.2c0 4.6 3.3 8.4 8 9.8 4.7-1.4 8-5.2 8-9.8V5.6z" />,
  <path d="M12 2.4 4 5.6v6.2c0 4.6 3.3 8.4 8 9.8 4.7-1.4 8-5.2 8-9.8V5.6z" />,
  "Shield",
);

export const ShieldCheck = make(
  <path d="M12 2.4 4 5.6v6.2c0 4.6 3.3 8.4 8 9.8 4.7-1.4 8-5.2 8-9.8V5.6z" />,
  <>
    <path d="M12 2.4 4 5.6v6.2c0 4.6 3.3 8.4 8 9.8 4.7-1.4 8-5.2 8-9.8V5.6z" />
    <path d="m8.6 11.8 2.4 2.4 4.4-4.6" />
  </>,
  "ShieldCheck",
);

export const ShieldOff = make(
  <path d="M12 2.4 4 5.6v6.2c0 4.6 3.3 8.4 8 9.8 4.7-1.4 8-5.2 8-9.8V5.6z" />,
  <>
    <path d="M12 2.4 4 5.6v6.2c0 4.6 3.3 8.4 8 9.8 4.7-1.4 8-5.2 8-9.8V5.6z" />
    <path d="M3.4 3.4 20.6 20.6" />
  </>,
  "ShieldOff",
);

export const Lock = make(
  <rect x="4" y="10.4" width="16" height="11" rx="2.6" />,
  <>
    <rect x="4" y="10.4" width="16" height="11" rx="2.6" />
    <path d="M7.8 10.4V7.6a4.2 4.2 0 0 1 8.4 0v2.8" />
    <path d="M12 14.6v2.6" />
  </>,
  "Lock",
);

export const CheckCircle2 = make(
  <circle cx="12" cy="12" r="9.4" />,
  <>
    <circle cx="12" cy="12" r="9.4" />
    <path d="m8 12.2 2.8 2.8L16 9.6" />
  </>,
  "CheckCircle2",
);

export const XCircle = make(
  <circle cx="12" cy="12" r="9.4" />,
  <>
    <circle cx="12" cy="12" r="9.4" />
    <path d="m9 9 6 6M15 9l-6 6" />
  </>,
  "XCircle",
);

export const Info = make(
  <circle cx="12" cy="12" r="9.4" />,
  <>
    <circle cx="12" cy="12" r="9.4" />
    <path d="M12 11v5.4M12 7.6h.01" />
  </>,
  "Info",
);

export const Siren = make(
  <path d="M6 19.4v-6.6a6 6 0 0 1 12 0v6.6z" />,
  <>
    <path d="M6 19.4v-6.6a6 6 0 0 1 12 0v6.6z" />
    <rect x="3.4" y="19.4" width="17.2" height="2.8" rx="1.4" />
    <path d="M12 3.4V1.6M4.6 6.2 3.4 5M19.4 6.2 20.6 5" />
  </>,
  "Siren",
);

export const Bell = make(
  <path d="M18 17.4H6l1.4-2.2V11a4.6 4.6 0 0 1 9.2 0v4.2z" />,
  <>
    <path d="M18 17.4H6l1.4-2.2V11a4.6 4.6 0 0 1 9.2 0v4.2z" />
    <path d="M10.2 20.4a2 2 0 0 0 3.6 0" />
  </>,
  "Bell",
);

export const MailWarning = make(
  <rect x="2.4" y="5" width="19.2" height="14" rx="2.6" />,
  <>
    <rect x="2.4" y="5" width="19.2" height="14" rx="2.6" />
    <path d="m3.4 6.4 8.6 6 8.6-6" />
    <path d="M18.4 13.6v2.2M18.4 18.4h.01" />
  </>,
  "MailWarning",
);

export const MailPlus = make(
  <rect x="2.4" y="5" width="19.2" height="14" rx="2.6" />,
  <>
    <rect x="2.4" y="5" width="19.2" height="14" rx="2.6" />
    <path d="m3.4 6.4 8.6 6 8.6-6" />
    <path d="M18.4 13v4.4M16.2 15.2h4.4" />
  </>,
  "MailPlus",
);

/* --------------------------------------------------------------- place */

export const Home = make(
  <path d="M3.6 10.4 12 3.4l8.4 7v9.2a1.6 1.6 0 0 1-1.6 1.6H5.2a1.6 1.6 0 0 1-1.6-1.6z" />,
  <>
    <path d="M3.6 10.4 12 3.4l8.4 7v9.2a1.6 1.6 0 0 1-1.6 1.6H5.2a1.6 1.6 0 0 1-1.6-1.6z" />
    <path d="M9.4 21.2v-6.4h5.2v6.4" />
  </>,
  "Home",
);
export const House = Home;

export const Building2 = make(
  <rect x="2.6" y="7.4" width="8.4" height="14" rx="1.6" />,
  <>
    <rect x="2.6" y="7.4" width="8.4" height="14" rx="1.6" />
    <path d="M11 21.4V4.2a1.6 1.6 0 0 1 1.6-1.6h6.8a1.6 1.6 0 0 1 1.6 1.6v17.2" />
    <path d="M14.4 6.6h3.4M14.4 10.4h3.4M14.4 14.2h3.4M5.6 11.6h2.4M5.6 15.4h2.4" />
  </>,
  "Building2",
);

export const MapPin = make(
  <path d="M12 21.6c4.2-4.6 6.4-7.9 6.4-10.6a6.4 6.4 0 1 0-12.8 0c0 2.7 2.2 6 6.4 10.6z" />,
  <>
    <path d="M12 21.6c4.2-4.6 6.4-7.9 6.4-10.6a6.4 6.4 0 1 0-12.8 0c0 2.7 2.2 6 6.4 10.6z" />
    <circle cx="12" cy="10.6" r="2.4" />
  </>,
  "MapPin",
);

export const LayoutGrid = make(
  <>
    <rect x="3" y="3" width="7.6" height="7.6" rx="1.8" />
    <rect x="13.4" y="13.4" width="7.6" height="7.6" rx="1.8" />
  </>,
  <>
    <rect x="3" y="3" width="7.6" height="7.6" rx="1.8" />
    <rect x="13.4" y="3" width="7.6" height="7.6" rx="1.8" />
    <rect x="3" y="13.4" width="7.6" height="7.6" rx="1.8" />
    <rect x="13.4" y="13.4" width="7.6" height="7.6" rx="1.8" />
  </>,
  "LayoutGrid",
);

export const Car = make(
  <path d="M3 18v-4.2l2-5.2A2 2 0 0 1 6.9 7.2h10.2a2 2 0 0 1 1.9 1.4l2 5.2V18z" />,
  <>
    <path d="M3 18v-4.2l2-5.2A2 2 0 0 1 6.9 7.2h10.2a2 2 0 0 1 1.9 1.4l2 5.2V18z" />
    <path d="M3 13.8h18" />
    <circle cx="7.2" cy="18" r="1.8" />
    <circle cx="16.8" cy="18" r="1.8" />
  </>,
  "Car",
);

/* -------------------------------------------------------------- people */

export const UserRound = make(
  <>
    <circle cx="12" cy="7.4" r="4.2" />
    <path d="M4.4 21.4a7.6 7.6 0 0 1 15.2 0z" />
  </>,
  <>
    <circle cx="12" cy="7.4" r="4.2" />
    <path d="M4.4 21.4a7.6 7.6 0 0 1 15.2 0" />
  </>,
  "UserRound",
);

export const Users = make(
  <>
    <circle cx="9.4" cy="7.4" r="3.8" />
    <path d="M2.6 20.6a6.8 6.8 0 0 1 13.6 0z" />
  </>,
  <>
    <circle cx="9.4" cy="7.4" r="3.8" />
    <path d="M2.6 20.6a6.8 6.8 0 0 1 13.6 0" />
    <path d="M16.4 4a3.8 3.8 0 0 1 0 7M18 14.4a6.8 6.8 0 0 1 3.4 6.2" />
  </>,
  "Users",
);

/* --------------------------------------------------------- content/doc */

export const FileText = make(
  <path d="M5.4 2.6h8L19.6 9v12.4a1 1 0 0 1-1 1H5.4a1 1 0 0 1-1-1V3.6a1 1 0 0 1 1-1z" />,
  <>
    <path d="M5.4 2.6h8L19.6 9v12.4a1 1 0 0 1-1 1H5.4a1 1 0 0 1-1-1V3.6a1 1 0 0 1 1-1z" />
    <path d="M13.4 2.6V9h6.2" />
    <path d="M7.8 13.4h7M7.8 17.2h5" />
  </>,
  "FileText",
);

export const FileDown = make(
  <path d="M5.4 2.6h8L19.6 9v12.4a1 1 0 0 1-1 1H5.4a1 1 0 0 1-1-1V3.6a1 1 0 0 1 1-1z" />,
  <>
    <path d="M5.4 2.6h8L19.6 9v12.4a1 1 0 0 1-1 1H5.4a1 1 0 0 1-1-1V3.6a1 1 0 0 1 1-1z" />
    <path d="M13.4 2.6V9h6.2" />
    <path d="M12 12.6v5.6M9.4 15.8 12 18.4l2.6-2.6" />
  </>,
  "FileDown",
);

export const ListChecks = make(
  <></>,
  <>
    <path d="m2.6 5 1.8 1.8L7.6 3.6M2.6 12l1.8 1.8 3.2-3.2M2.6 19l1.8 1.8 3.2-3.2" />
    <path d="M11 5.4h10.4M11 12.4h10.4M11 19.4h10.4" />
  </>,
  "ListChecks",
);

export const Lightbulb = make(
  <path d="M12 2.6a6.6 6.6 0 0 1 4 11.8V17H8v-2.6a6.6 6.6 0 0 1 4-11.8z" />,
  <>
    <path d="M12 2.6a6.6 6.6 0 0 1 4 11.8V17H8v-2.6a6.6 6.6 0 0 1 4-11.8z" />
    <path d="M9.4 20h5.2M10 22.2h4" />
  </>,
  "Lightbulb",
);

export const Sparkles = make(
  <path d="m12 2.6 2.2 5.6 5.6 2.2-5.6 2.2L12 18.2 9.8 12.6 4.2 10.4l5.6-2.2z" />,
  <>
    <path d="m12 2.6 2.2 5.6 5.6 2.2-5.6 2.2L12 18.2 9.8 12.6 4.2 10.4l5.6-2.2z" />
    <path d="M18.6 16.4l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z" />
  </>,
  "Sparkles",
);
